export type SelectItem = {
  id: number | string
  title: string
}

/**
 * Safe label for Autocomplete — NEVER returns [object Object].
 * Handles objects with title/name/label, primitives, and invalid values.
 */
export function getSelectItemOptionLabel(option: unknown): string {
  if (option == null) return ''
  if (typeof option === 'string') return option
  if (typeof option === 'number') return String(option)
  if (Array.isArray(option)) return ''
  if (typeof option === 'object') {
    const o = option as Record<string, unknown>
    const s =
      (typeof o['title'] === 'string' && o['title']) ||
      (typeof o['name'] === 'string' && o['name']) ||
      (typeof o['label'] === 'string' && o['label'])
    if (s) return s
    if (o['id'] != null) return String(o['id'])
  }
  return ''
}

/**
 * Normalize any option to { id, title } so stored form value always displays
 * correctly and never flashes [object Object].
 */
export function normalizeSelectItem(option: unknown): SelectItem {
  if (option == null) return { id: '', title: '' }
  if (typeof option === 'object' && option !== null) {
    const o = option as Record<string, unknown>
    const id = o['id'] != null ? o['id'] : ''
    const title = getSelectItemOptionLabel(option) || String(id)
    return { id: id as number | string, title }
  }
  const s = getSelectItemOptionLabel(option)
  return { id: s, title: s }
}

/** Normalize array of options for multi-select value. */
export function normalizeSelectItems(options: unknown): SelectItem[] {
  if (!Array.isArray(options)) return []
  return options.map(normalizeSelectItem)
}

/** Compare by id for Autocomplete isOptionEqualToValue — fixes chip X needing two clicks when value is normalized. */
export function isSelectItemEqual(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  const o1 = a as Record<string, unknown>
  const o2 = b as Record<string, unknown>
  return o1['id'] !== undefined && o1['id'] === o2['id']
}

/** Keys that are raw id arrays (e.g. selectedGuids), not SelectItem[] — do not normalize. */
const RAW_ARRAY_KEYS = new Set(['selectedGuids'])

/** Normalize array fields that are SelectItem[] so form never holds raw API objects (fixes [object Object] and chip X). Leaves selectedGuids etc. as-is. */
export function normalizeFilterParams(
  params: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (params == null) return {}
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(params)) {
    const v = params[key]
    if (Array.isArray(v) && !RAW_ARRAY_KEYS.has(key)) {
      out[key] = normalizeSelectItems(v)
    } else {
      out[key] = v
    }
  }
  return out
}
